/**
 * SyringeViz — horizontal insulin-syringe SVG with calibration marks and a fill line.
 *
 * Orientation matches a real insulin syringe:
 *   - Plunger flange on the LEFT (back)
 *   - Needle on the RIGHT (front)
 *   - Tick labels: 100 at plunger end, 0 at needle end (reads from rubber stopper position)
 *   - Liquid fills FROM the needle end (right) growing LEFTWARD as units increase
 *   - The plunger rubber stopper sits at the LEFT edge of the liquid → that's where you read your dose
 */
export default function SyringeViz({ units = 0, maxUnits = 100, color = "#CCFF00" }) {
  const safe = Math.max(0, Math.min(units, maxUnits));
  const W = 600;
  const H = 160;
  const bodyX = 40;
  const bodyY = 60;
  const bodyW = 420;
  const bodyH = 40;
  const fillW = (safe / maxUnits) * bodyW;
  // Liquid sits at the NEEDLE end (right). Left edge of liquid = where plunger stopper rests.
  const liquidX = bodyX + bodyW - fillW;
  const ticks = Array.from({ length: 11 }, (_, i) => i);
  const overflow = safe >= maxUnits;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-2xl mx-auto" aria-hidden="true">
      <defs>
        <linearGradient id="liquid" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.95" />
          <stop offset="100%" stopColor={color} stopOpacity="0.65" />
        </linearGradient>
        <linearGradient id="glass" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#1A1A1A" />
          <stop offset="50%" stopColor="#0A0A0A" />
          <stop offset="100%" stopColor="#1A1A1A" />
        </linearGradient>
      </defs>

      {/* Plunger rod (left = back) */}
      <rect x="6" y={bodyY + bodyH / 2 - 6} width="36" height="12" fill="#262626" rx="2" />
      <rect x="2" y={bodyY + bodyH / 2 - 14} width="8" height="28" fill="#3A3A3A" rx="1" />

      {/* Body glass */}
      <rect x={bodyX} y={bodyY} width={bodyW} height={bodyH} fill="url(#glass)" stroke="#333" strokeWidth="1" rx="2" />

      {/* Liquid fill — anchored at the needle end (right), grows leftward */}
      <rect x={liquidX} y={bodyY} width={fillW} height={bodyH} fill="url(#liquid)" rx="2" />

      {/* Plunger rubber stopper — sits at the LEFT edge of the liquid */}
      <rect x={liquidX - 6} y={bodyY + 2} width="6" height={bodyH - 4} fill="#3A3A3A" />

      {/* Calibration marks — labels reverse: 100 at plunger end, 0 at needle end */}
      {ticks.map((t) => {
        const x = bodyX + (t / 10) * bodyW;
        const isMajor = t % 5 === 0;
        const labelValue = Math.round(((10 - t) / 10) * maxUnits); // reversed
        return (
          <g key={t}>
            <line
              x1={x}
              y1={bodyY - 2}
              x2={x}
              y2={bodyY + (isMajor ? -10 : -6)}
              stroke="#A3A3A3"
              strokeWidth={isMajor ? 1.4 : 1}
            />
            {isMajor && (
              <text
                x={x}
                y={bodyY - 16}
                fill="#A3A3A3"
                fontSize="11"
                textAnchor="middle"
                fontFamily="JetBrains Mono, monospace"
              >
                {labelValue}
              </text>
            )}
          </g>
        );
      })}

      {/* Needle hub */}
      <rect x={bodyX + bodyW} y={bodyY + bodyH / 2 - 8} width="20" height="16" fill="#3A3A3A" />
      {/* Needle */}
      <line
        x1={bodyX + bodyW + 20}
        y1={bodyY + bodyH / 2}
        x2={W - 10}
        y2={bodyY + bodyH / 2}
        stroke="#999"
        strokeWidth="3"
        strokeLinecap="round"
      />

      {/* Fill indicator line + label at the plunger-stopper position (read line) */}
      <g>
        <line
          x1={liquidX}
          y1={bodyY - 26}
          x2={liquidX}
          y2={bodyY + bodyH + 22}
          stroke={overflow ? "#FF3366" : color}
          strokeWidth="2"
          strokeDasharray={overflow ? "4 4" : "0"}
        />
        <rect
          x={Math.min(W - 70, Math.max(2, liquidX - 34))}
          y={bodyY + bodyH + 22}
          width="68"
          height="22"
          rx="3"
          fill={overflow ? "#FF3366" : color}
        />
        <text
          x={Math.min(W - 36, Math.max(36, liquidX))}
          y={bodyY + bodyH + 37}
          fill="#000"
          fontSize="12"
          fontWeight="700"
          textAnchor="middle"
          fontFamily="JetBrains Mono, monospace"
        >
          {overflow ? "OVERFLOW" : `DRAW TO ${Math.round(safe * 10) / 10}`}
        </text>
      </g>

      {/* Capacity label */}
      <text x={W - 10} y={H - 8} fill="#666" fontSize="10" textAnchor="end" fontFamily="JetBrains Mono, monospace">
        U-{maxUnits} insulin syringe — read at the plunger stopper
      </text>
    </svg>
  );
}
